import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useServerFn } from '@tanstack/react-start';
import { Link, useLocation } from '@/lib/router-compat';
import { Button } from '@/components/ui/button';
import { ChevronDown, ChevronRight, LayoutGrid } from 'lucide-react';
import { getNavCategories, getNavDeals, type NavCategory, type NavDealsResult } from '@/lib/navigation.functions';
import { SmartImage } from '@/components/common/SmartImage';
import { activeCycle, hoverBgCycle, hoverTextCycle, textCycle } from '@/lib/menuColors';



// Categorias fixas primeiro; demais por popularidade (contagem real de produtos).
const pinnedOrder = ['moda', 'eletronicos', 'eletrodomesticos', 'beleza-e-saude'];

const NAV_KEY = 'nav-categories-v1';
function useCategories() {
  const fetchCategories = useServerFn(getNavCategories);
  const q = useQuery({
    queryKey: ['navigation-categories'],
    queryFn: async () => {
      const rows = await fetchCategories();
      if (rows.length) { try { localStorage.setItem(NAV_KEY, JSON.stringify(rows)); } catch { /* ignore */ } return rows; }
      // Resposta vazia (falha transitória): mantém a última lista boa.
      try { const saved = JSON.parse(localStorage.getItem(NAV_KEY) || '[]'); if (saved.length) return saved as NavCategory[]; } catch { /* ignore */ }
      throw new Error('empty categories');
    },
    staleTime: 60 * 60_000, gcTime: 24 * 60 * 60_000, retry: 3, refetchOnWindowFocus: false,
    placeholderData: (prev) => prev,
  });
  const [saved, setSaved] = useState<NavCategory[]>([]);
  useEffect(() => { try { setSaved(JSON.parse(localStorage.getItem(NAV_KEY) || '[]')); } catch { /* ignore */ } }, []);
  return { ...q, data: q.data?.length ? q.data : saved };
}

export function CategoryMegaMenu({ interactionActive = false }: { interactionActive?: boolean }) {
  const { data = [] } = useCategories();
  const fetchDeals = useServerFn(getNavDeals);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState('');
  const location = useLocation();
  const region = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Fora da home, a faixa de categorias fica oculta e só aparece quando o
  // mouse (ou o foco de teclado) entra na área do cabeçalho.
  const isHome = location.pathname === '/';
  const [barVisible, setBarVisible] = useState(false);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (isHome) return;
    const header = region.current?.closest('header');
    if (!header) return;
    let disposed = false;
    const cancelHide = () => {
      if (hideTimer.current) clearTimeout(hideTimer.current);
      hideTimer.current = null;
    };
    const isEngaged = () => interactionActive || open || header.matches(':hover') || header.contains(document.activeElement);
    const update = () => {
      if (disposed) return;
      if (isEngaged()) {
        cancelHide();
        setBarVisible(true);
      } else if (!hideTimer.current) {
        hideTimer.current = setTimeout(() => {
          hideTimer.current = null;
          if (!isEngaged()) setBarVisible(false);
        }, 600);
      }
    };
    // Read focus after the browser moves it, not during focusout.
    const afterFocus = () => queueMicrotask(update);
    update();
    header.addEventListener('pointerenter', update);
    header.addEventListener('pointerleave', update);
    document.addEventListener('focusin', afterFocus);
    document.addEventListener('focusout', afterFocus);
    document.addEventListener('pointermove', update, { passive: true });
    return () => {
      disposed = true;
      header.removeEventListener('pointerenter', update);
      header.removeEventListener('pointerleave', update);
      document.removeEventListener('focusin', afterFocus);
      document.removeEventListener('focusout', afterFocus);
      document.removeEventListener('pointermove', update);
      cancelHide();
    };
  }, [isHome, interactionActive, open, location.pathname]);
  const barShown = isHome || barVisible || open;
  // Altura real da faixa: o navegador não consegue animar até "auto".
  const collapse = useRef<HTMLDivElement>(null);
  const [barHeight, setBarHeight] = useState(48);
  useEffect(() => {
    const inner = collapse.current?.firstElementChild as HTMLElement | null;
    if (!inner) return;
    const measure = () => setBarHeight(inner.offsetHeight || 48);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(inner);
    return () => observer.disconnect();
  }, []);

  const roots = useMemo(() => data.filter(c => !c.parent_id), [data]);
  const featured = useMemo(() => {
    const total = (c: NavCategory) => c.count + data.filter(s => s.parent_id === c.id).reduce((sum, s) => sum + s.count, 0);
    const pinned = pinnedOrder.map(slug => roots.find(c => c.slug === slug)).filter((c): c is NavCategory => !!c);
    const rest = roots.filter(c => !pinnedOrder.includes(c.slug) && c.slug !== 'outros').sort((a, b) => total(b) - total(a));
    return [...pinned, ...rest].slice(0, 8);
  }, [roots, data]);
  const current = roots.find(c => c.slug === selected) ?? roots[0];
  const children = data.filter(c => c.parent_id === current?.id);
  const [hoveredSub, setHoveredSub] = useState('');
  const subTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pickSub = (slug: string) => { if (subTimer.current) clearTimeout(subTimer.current); subTimer.current = setTimeout(() => setHoveredSub(slug), 120); };
  useEffect(() => { setHoveredSub(''); }, [current?.slug]);
  useEffect(() => () => { if (subTimer.current) clearTimeout(subTimer.current); }, []);
  const dealsKey = `nav-deals-v1:${current?.slug ?? ''}|${hoveredSub}`;
  const { data: result } = useQuery({
    queryKey: ['navigation-deals', current?.slug, hoveredSub],
    queryFn: async () => {
      const res = await fetchDeals({ data: { category: current?.slug ?? '', sub: hoveredSub } });
      if (res.items.length) { try { localStorage.setItem(dealsKey, JSON.stringify(res)); } catch { /* ignore */ } return res; }
      try { const saved = JSON.parse(localStorage.getItem(dealsKey) || 'null'); if (saved?.items?.length) return saved as NavDealsResult; } catch { /* ignore */ }
      return res;
    },
    enabled: open && !!current,
    staleTime: 5 * 60_000, gcTime: 60 * 60_000, retry: 2, refetchOnWindowFocus: false,
    initialData: () => { try { const s = JSON.parse(localStorage.getItem(dealsKey) || 'null'); return s?.items?.length ? s as NavDealsResult : undefined; } catch { return undefined; } },
    initialDataUpdatedAt: 0,
    placeholderData: (prev: NavDealsResult | undefined) => prev,
  });
  const deals = result?.items ?? [];
  const isProducts = result?.mode === 'products';
  // Pré-carrega as imagens dos destaques assim que os dados chegam.
  useEffect(() => { for (const d of deals) { const img = new Image(); img.src = d.image_url; } }, [deals]);
  useEffect(() => { setOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') setOpen(false); };
    const outside = (event: PointerEvent) => { if (region.current && !region.current.contains(event.target as Node)) setOpen(false); };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', outside);
    return () => { document.removeEventListener('keydown', onKey); document.removeEventListener('pointerdown', outside); };
  }, [open]);
  useEffect(() => () => { if (closeTimer.current) clearTimeout(closeTimer.current); }, []);
  const startClose = () => { closeTimer.current = setTimeout(() => setOpen(false), 180); };
  const cancelClose = () => { if (closeTimer.current) clearTimeout(closeTimer.current); };

  return <div ref={region} className="relative hidden bg-background lg:block" onMouseEnter={cancelClose} onMouseLeave={startClose}>
    <div ref={collapse} className="nav-bar-collapse" style={{ height: barShown ? barHeight : 0 }}>
      <div className="relative">
        <div className={`nav-bar-content container flex h-12 items-center gap-1 xl:gap-3 ${barShown ? 'translate-y-0 opacity-100' : '-translate-y-1 opacity-0'}`}>

      <Button type="button" variant="ghost" aria-expanded={open} aria-controls="category-mega-panel" onClick={() => setOpen(v => !v)} className="shrink-0 gap-2 pl-0 font-semibold">
        <LayoutGrid className="h-4 w-4 text-primary" /> Todas as categorias <ChevronDown className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} />

      </Button>
      <span className="mx-2 h-5 border-l" aria-hidden="true" />
      <nav aria-label="Categorias em destaque" className="flex min-w-0 flex-1 items-center justify-between gap-1">
        {featured.map((cat, i) => <Link key={cat.id} onMouseEnter={isHome ? () => { setSelected(cat.slug); setOpen(true); } : undefined} onFocus={isHome ? () => { setSelected(cat.slug); setOpen(true); } : undefined} to={`/ofertas/${cat.slug}`} className={`${i >= 6 ? 'hidden 2xl:block' : ''} max-w-40 truncate rounded px-2 py-2 text-sm font-medium ${hoverBgCycle[i % 4]} focus-visible:outline-2 focus-visible:outline-primary`} title={cat.name}>{cat.name}</Link>)}
      </nav>
        </div>
        <span aria-hidden="true" className={`absolute inset-x-0 top-0 h-px bg-border transition-opacity duration-200 ${barShown ? 'opacity-100' : 'opacity-0'}`} />
        <span aria-hidden="true" className="absolute inset-x-0 bottom-0 h-[3px] bg-[linear-gradient(90deg,var(--color-brand-green),var(--color-brand-red),var(--color-brand-blue),var(--color-brand-yellow))]" />
      </div>
    </div>
    {open && <div id="category-mega-panel" className="absolute inset-x-0 top-full z-60 animate-in fade-in slide-in-from-top-1 duration-150 border-y bg-background shadow-xl" onMouseEnter={cancelClose}>

      <div className="container grid max-h-[min(570px,75vh)] grid-cols-[210px_minmax(0,1fr)_260px] overflow-y-auto py-5 xl:grid-cols-[240px_minmax(0,1fr)_320px]">
        <nav aria-label="Todas as categorias" className="border-r pr-3">
          <p className="mb-2 px-3 text-xs font-bold uppercase text-muted-foreground">Explorar</p>
          {roots.map((cat, idx) => <Link key={cat.id} to={`/ofertas/${cat.slug}`} onMouseEnter={() => setSelected(cat.slug)} onFocus={() => setSelected(cat.slug)} aria-current={current?.id === cat.id ? 'true' : undefined} className={`flex items-center justify-between rounded px-3 py-2 text-sm font-medium focus-visible:outline-2 focus-visible:outline-primary ${current?.id === cat.id ? activeCycle[idx % 4] : hoverBgCycle[idx % 4]}`}>{cat.name}<ChevronRight className="h-4 w-4 shrink-0" /></Link>)}
        </nav>
        <div className="min-w-0 px-6 xl:px-9">
          {current ? <><Link to={`/ofertas/${current.slug}`} onMouseEnter={() => pickSub('')} onFocus={() => pickSub('')} className="inline-flex items-center gap-1 text-xl font-bold hover:text-brand-blue">{current.name} <ChevronRight className="h-5 w-5" /></Link>
            <div className="mt-5 grid grid-cols-2 gap-x-7 gap-y-3 xl:grid-cols-3">{children.map((sub, idx) => <Link key={sub.id} to={`/ofertas/${current.slug}/${sub.slug}`} onMouseEnter={() => pickSub(sub.slug)} onFocus={() => pickSub(sub.slug)} aria-current={hoveredSub === sub.slug ? 'true' : undefined} className={`min-w-0 text-sm ${hoverTextCycle[idx % 4]} hover:underline ${hoveredSub === sub.slug ? 'font-semibold' : ''}`}>{sub.name}</Link>)}</div>
            {children.length === 0 && <Link to={`/ofertas/${current.slug}`} className="mt-5 block text-sm text-brand-blue hover:underline">Ver produtos de {current.name}</Link>}
          </> : <Link to="/ofertas" className="text-sm font-semibold text-brand-blue">Ver todas as ofertas</Link>}
        </div>
        <aside className="w-72 shrink-0 border-l pl-5" aria-label="Ofertas da categoria">
          <p className="mb-3 text-xs font-bold uppercase text-muted-foreground">{isProducts ? 'Produtos em destaque' : 'Ofertas em destaque'}</p>
          {deals.length ? <div className="space-y-1.5">{deals.map((deal, i) => <Link key={deal.slug} to={`/produto/${deal.slug}`} className={`flex items-center gap-3 rounded-lg p-2 ${hoverBgCycle[i % 4]}`}>
            <span className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded bg-muted/30"><SmartImage url={deal.image_url} className="h-full w-full object-contain" w={160} h={160} /></span>
            <span className="min-w-0 flex-1"><span className="line-clamp-2 text-xs font-medium leading-4">{deal.title}</span><span className="mt-0.5 flex items-baseline gap-1.5"><span className="text-sm font-bold text-foreground">{deal.price.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</span>{!isProducts && <span className="text-xs text-muted-foreground line-through">{deal.original_price.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</span>}</span><span className="flex items-center gap-1.5">{!isProducts && <span className={`text-base font-extrabold ${textCycle[i % 4]}`}>-{deal.discount_percent}%</span>}<span className="truncate text-[11px] text-muted-foreground">{deal.store}</span></span></span>
          </Link>)}</div> : result ? <p className="text-sm text-muted-foreground">Explore os produtos e lojas desta categoria.</p> : <div className="space-y-1.5" aria-hidden="true">{[0, 1, 2, 3].map(i => <div key={i} className="flex items-center gap-3 p-2"><span className="h-16 w-16 shrink-0 animate-pulse rounded bg-muted" /><span className="flex-1 space-y-2"><span className="block h-3 w-full animate-pulse rounded bg-muted" /><span className="block h-3 w-2/3 animate-pulse rounded bg-muted" /></span></div>)}</div>}
        </aside>
      </div>
    </div>}
  </div>;
}

export function MobileCategories({ onNavigate }: { onNavigate: () => void }) {
  const { data = [] } = useCategories();
  const roots = data.filter(cat => !cat.parent_id);
  if (!roots.length) return null;
  return <div className="border-t pt-3"><p className="mb-2 text-xs font-semibold uppercase text-muted-foreground">Categorias</p>{roots.map(cat => {
    const children: NavCategory[] = data.filter(sub => sub.parent_id === cat.id);
    return <details key={cat.id} className="border-b"><summary className="cursor-pointer py-3 text-sm font-medium">{cat.name}</summary><div className="flex flex-col gap-1 pb-3 pl-3"><Link to={`/ofertas/${cat.slug}`} onClick={onNavigate} className="py-2 text-sm font-semibold text-primary">Ver tudo em {cat.name}</Link>{children.map(sub => <Link key={sub.id} to={`/ofertas/${cat.slug}/${sub.slug}`} onClick={onNavigate} className="py-2 text-sm">{sub.name}</Link>)}</div></details>;
  })}</div>;
}
